import React, { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ImageOff, Bot, BadgeCheck, Loader2, Wand2, Pencil, Sparkles, PlayCircle, CheckCircle2, FileText, Mic, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusPill } from "@/components/StatusPill";
import AIAssistantPanel from "@/components/AIAssistantPanel";
import { WhatsAppShareButton } from "@/components/vehicle/WhatsAppShareButton";
import OverviewTab from "@/components/vehicle/OverviewTab";
import DataTab from "@/components/vehicle/DataTab";
import PhotosTab from "@/components/vehicle/PhotosTab";
import ConditionTab from "@/components/vehicle/ConditionTab";
import DescriptionTab from "@/components/vehicle/DescriptionTab";
import PublishTab from "@/components/vehicle/PublishTab";
import HistoryTab from "@/components/vehicle/HistoryTab";
import MargeTab from "@/components/vehicle/MargeTab";
import { api, apiError, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { SIMPLE_STATUS_META, AVAILABILITY_META, fmtEUR, fmtKm, SALES_CHANNELS, SALES_CHANNEL_LABELS } from "@/lib/constants";

// Vereinfachte Statuswahl (interne Status bleiben technisch bestehen)
const STATUS_CHOICES = [
  { key: "entwurf", label: "Entwurf" },
  { key: "fotografie", label: "Fotos" },
  { key: "datenpruefung", label: "Prüfung" },
  { key: "bereit", label: "Veröffentlichungsbereit" },
  { key: "veroeffentlicht", label: "Online" },
  { key: "archiviert", label: "Archiviert" },
];

export default function VehicleDetailPage() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [datacheck, setDatacheck] = useState(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [soldOpen, setSoldOpen] = useState(false);
  const [soldPrice, setSoldPrice] = useState("");
  const [soldDate, setSoldDate] = useState("");
  const [soldChannel, setSoldChannel] = useState("");
  const [soldCountry, setSoldCountry] = useState("");
  const [soldSummary, setSoldSummary] = useState(null);
  const [busy, setBusy] = useState(false);
  const { user } = useAuth();
  const isAdmin = ["owner", "manager"].includes(user?.role);
  // Verkaufspreis-Dialog (mit KI-Vorschlag)
  const [priceOpen, setPriceOpen] = useState(false);
  const [priceValue, setPriceValue] = useState("");
  const [priceBusy, setPriceBusy] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  const confirmDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success(`Fahrzeug ${vehicle?.specs?.marke || ""} ${vehicle?.specs?.modell || ""} (${vehicle?.bestandsnummer}) gelöscht`);
      navigate("/app/fahrzeuge");
    } catch (e) {
      toast.error(apiError(e, "Löschen fehlgeschlagen"));
      setDeleteBusy(false);
    }
  };

  const downloadExpose = async () => {
    setPdfBusy(true);
    try {
      await downloadFile(`/vehicles/${id}/export/pdf`, `Expose_${vehicle?.bestandsnummer || "fahrzeug"}.pdf`);
      toast.success("PDF-Exposé heruntergeladen");
    } catch (e) {
      toast.error("PDF-Export fehlgeschlagen. Bitte erneut versuchen.");
    } finally { setPdfBusy(false); }
  };
  const { t } = useLang();
  const tab = searchParams.get("tab") || "overview";

  // Aktiven Tab auf Mobile automatisch in Sicht scrollen
  useEffect(() => {
    const el = document.querySelector(`[data-testid="vehicle-tab-${tab}"]`);
    if (el) el.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [tab]);

  // Schnellzugriff: Notiz diktieren (springt zum Zustand-Tab und zur Notiz-Box)
  const goToNotes = () => {
    setSearchParams({ tab: "condition" });
    setTimeout(() => {
      document.querySelector('[data-testid="condition-new-note-box"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 350);
  };

  const reload = useCallback(async () => {
    try {
      const [v, dc] = await Promise.all([
        api.get(`/vehicles/${id}`),
        api.get(`/vehicles/${id}/datacheck`),
      ]);
      setVehicle(v.data);
      setDatacheck(dc.data);
    } catch (e) {
      toast.error(apiError(e, "Fahrzeug konnte nicht geladen werden"));
    }
  }, [id]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    if (searchParams.get("sold") === "1" && vehicle) {
      openSoldDialog();
    }
    // eslint-disable-next-line
  }, [vehicle?.id]);

  const openSoldDialog = () => {
    setSoldPrice(vehicle?.price != null ? String(vehicle.price) : "");
    setSoldDate(new Date().toISOString().slice(0, 10));
    setSoldChannel("");
    setSoldCountry("");
    setSoldSummary(null);
    setSoldOpen(true);
  };

  const changeStatus = async (status) => {
    try {
      await api.post(`/vehicles/${id}/status`, { status });
      toast.success("Status aktualisiert");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };

  const markSold = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/vehicles/${id}/sold`, {
        sale_price: soldPrice ? parseFloat(soldPrice) : null,
        sale_date: soldDate || null,
        sale_channel: soldChannel || null,
        sale_country: soldCountry || null,
      });
      toast.success(`Fahrzeug als verkauft markiert. ${r.data.deactivated_channels.length} Kanal/Kanäle deaktiviert.`);
      setSoldSummary(r.data.sold_summary || null);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const openPriceDialog = () => {
    setPriceValue(vehicle?.price != null ? String(vehicle.price) : "");
    setPriceOpen(true);
  };

  const savePrice = async () => {
    const p = parseFloat(priceValue);
    if (!priceValue || Number.isNaN(p) || p <= 0) {
      toast.error("Bitte einen gültigen Verkaufspreis eingeben.");
      return;
    }
    setPriceBusy(true);
    try {
      await api.patch(`/vehicles/${id}`, { price: p, source: "manuell" });
      toast.success(`Verkaufspreis gespeichert: ${fmtEUR(p)}`);
      setPriceOpen(false);
      reload();
    } catch (e) { toast.error(apiError(e)); } finally { setPriceBusy(false); }
  };

  const generateSuggestion = async () => {
    setSuggestBusy(true);
    try {
      const r = await api.post(`/vehicles/${id}/price-suggestion`, {});
      setVehicle((v) => ({ ...v, price_suggestion: r.data }));
    } catch (e) {
      toast.error(apiError(e, "Der KI-Preisvorschlag ist momentan nicht verfügbar"));
    } finally { setSuggestBusy(false); }
  };

  if (!vehicle || !datacheck)
    return (
      <div className="space-y-4">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-48" />
        <Skeleton className="h-96" />
      </div>
    );

  const s = vehicle.specs || {};
  const pubCount = Object.values(vehicle.publications || {}).filter((p) => p.status === "published").length;
  const simpleKey = vehicle.status === "verkauft" || vehicle.status === "ausgeliefert" ? "verkauft"
    : vehicle.status === "veroeffentlicht" || vehicle.status === "reserviert" ? "online"
    : vehicle.status === "bereit" ? "bereit"
    : vehicle.status === "datenpruefung" || vehicle.status === "preisfreigabe" ? "pruefung"
    : vehicle.status === "archiviert" ? "archiviert"
    : vehicle.status === "entwurf" || vehicle.status === "angekauft" ? "entwurf" : "fotos";
  const statusMeta = SIMPLE_STATUS_META[simpleKey];

  const TABS = [
    { key: "overview", label: t("tab.overview") },
    { key: "data", label: t("tab.data"), badge: datacheck.conflicts.length + datacheck.unconfirmed_claims.length },
    { key: "photos", label: t("tab.photos") },
    { key: "condition", label: t("tab.condition"), badge: (vehicle.damages || []).filter((d) => d.repair_status === "offen").length },
    { key: "description", label: t("tab.description") },
    { key: "publish", label: t("tab.publish") },
    ...(isAdmin ? [{ key: "market", label: "Markt & Marge" }] : []),
    { key: "history", label: t("tab.history") },
  ];

  return (
    <div className="space-y-5 pb-10">
      <Link to="/app/fahrzeuge" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="vehicle-back-link">
        <ChevronLeft className="h-4 w-4" /> {t("nav.vehicles")}
      </Link>

      {/* Hero */}
      <div className="rounded-2xl border border-border bg-card overflow-hidden">
        <div className="flex flex-col md:flex-row">
          {vehicle.cover_image_url ? (
            <img src={vehicle.cover_image_url} alt="" className="md:w-[340px] h-52 md:h-auto object-cover" />
          ) : (
            <div className="md:w-[340px] h-52 bg-secondary flex flex-col items-center justify-center gap-2 text-muted-foreground">
              <ImageOff className="h-6 w-6" /><span className="text-xs">Keine Fotos vorhanden</span>
            </div>
          )}
          <div className="flex-1 p-5 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h1 className="font-display text-xl lg:text-2xl font-bold" data-testid="vehicle-title">{s.marke} {s.modell}</h1>
                <div className="text-sm text-muted-foreground mt-0.5">
                  <span className="font-mono">{vehicle.bestandsnummer}</span> · EZ {s.erstzulassung || "–"} · {fmtKm(s.kilometerstand)} · {s.standort || "–"}
                </div>
              </div>
              <div className="text-right">
                <button type="button" onClick={openPriceDialog}
                  className="group inline-flex items-center gap-1.5 rounded-lg px-2 py-1 -mr-2 hover:bg-secondary/60 transition-colors"
                  title="Verkaufspreis bearbeiten" data-testid="vehicle-price-edit-button">
                  {vehicle.price != null ? (
                    <span className="font-display text-2xl font-bold tabular-nums" data-testid="vehicle-price">{fmtEUR(vehicle.price)}</span>
                  ) : (
                    <span className="text-sm font-semibold text-primary" data-testid="vehicle-price">Preis eintragen</span>
                  )}
                  <Pencil className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
                </button>
                {vehicle.verfuegbarkeit && vehicle.verfuegbarkeit !== "sofort" && (
                  <div className="text-xs text-sky-300">{AVAILABILITY_META[vehicle.verfuegbarkeit]?.label}{vehicle.verfuegbarkeit === "ab_datum" && vehicle.verfuegbar_ab ? ` (${vehicle.verfuegbar_ab})` : ""}</div>
                )}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill label={statusMeta?.label || vehicle.status} color={statusMeta?.color} testId="vehicle-status-pill" />
              {vehicle.is_demo && <StatusPill label="Demo-Datensatz" color="bg-sky-500/15 text-sky-300 border-sky-500/30" testId="vehicle-demo-pill" />}
              <StatusPill label={`Daten ${datacheck.completeness_pct}%`} color={datacheck.completeness_pct >= 80 ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"} />
              {pubCount > 0 && <StatusPill label={`${pubCount} Kanal/K. online`} color="bg-emerald-500/15 text-emerald-300 border-emerald-500/30" />}
              {datacheck.conflicts.length > 0 && <StatusPill label={`${datacheck.conflicts.length} Konflikt(e)`} color="bg-red-500/15 text-red-300 border-red-500/30" />}
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {["entwurf", "angekauft", "angekommen", "aufbereitung", "fotografie", "datenpruefung", "preisfreigabe", "bereit"].includes(vehicle.status) && (
                <Button size="sm" className="gap-1.5 font-semibold h-10" onClick={() => navigate(`/app/aufnehmen?vehicle=${vehicle.id}`)} data-testid="vehicle-resume-button">
                  <PlayCircle className="h-4 w-4" /> Aufnahme fortsetzen
                </Button>
              )}
              <Button size="sm" variant={["entwurf", "angekauft", "angekommen", "aufbereitung", "fotografie", "datenpruefung", "preisfreigabe", "bereit"].includes(vehicle.status) ? "outline" : "default"} className="gap-1.5 font-semibold h-10" onClick={() => navigate(`/app/fotobooth?vehicle=${vehicle.id}`)} data-testid="vehicle-fotobooth-button">
                <Wand2 className="h-4 w-4" /> Fotobooth öffnen
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 h-10" disabled={pdfBusy} onClick={downloadExpose} data-testid="vehicle-expose-button">
                {pdfBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />} PDF-Exposé
              </Button>
              <WhatsAppShareButton vehicle={vehicle} testId="vehicle-whatsapp-button" />
              <Button size="sm" variant="outline" className="gap-1.5 h-10" onClick={goToNotes} data-testid="vehicle-dictate-button">
                <Mic className="h-4 w-4" /> Notiz diktieren
              </Button>
              <Button size="sm" variant="outline" className="gap-1.5 h-10" onClick={() => setAssistantOpen(true)} data-testid="vehicle-assistant-button">
                <Bot className="h-4 w-4 text-primary" /> {t("assistant.title")}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" variant="outline" className="h-10" data-testid="vehicle-status-button">Status ändern</Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  {STATUS_CHOICES.map((c) => (
                    <DropdownMenuItem key={c.key} disabled={c.key === vehicle.status} onClick={() => changeStatus(c.key)} data-testid={`vehicle-status-${c.key}`}>{c.label}</DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {vehicle.status !== "verkauft" && (
                <Button size="sm" variant="secondary" className="gap-1.5" onClick={openSoldDialog} data-testid="vehicle-sold-button">
                  <BadgeCheck className="h-4 w-4" /> Fahrzeug verkauft
                </Button>
              )}
              {isAdmin && (
                <Button size="sm" variant="outline" className="gap-1.5 h-10 border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                  onClick={() => setDeleteOpen(true)} data-testid="vehicle-delete-button">
                  <Trash2 className="h-4 w-4" /> Löschen
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v })}>
        <div className="relative">
          <TabsList className="flex w-full overflow-x-auto justify-start h-auto p-1 scrollbar-none" data-testid="vehicle-tabs">
            {TABS.map((tb) => (
              <TabsTrigger key={tb.key} value={tb.key} data-testid={`vehicle-tab-${tb.key}`} className="whitespace-nowrap min-h-[40px]">
                {tb.label}
                {tb.badge > 0 && <span className="ml-1.5 rounded-full bg-amber-500/20 text-amber-300 text-[10px] px-1.5">{tb.badge}</span>}
              </TabsTrigger>
            ))}
          </TabsList>
          <div className="pointer-events-none absolute inset-y-0 right-0 w-12 rounded-r-md bg-gradient-to-l from-background/90 to-transparent lg:hidden" aria-hidden="true" />
        </div>
        <div className="mt-4">
          <TabsContent value="overview"><OverviewTab vehicle={vehicle} datacheck={datacheck} reload={reload} goTab={(v) => setSearchParams({ tab: v })} /></TabsContent>
          <TabsContent value="data"><DataTab vehicle={vehicle} datacheck={datacheck} reload={reload} onEditPrice={openPriceDialog} /></TabsContent>
          <TabsContent value="photos"><PhotosTab vehicle={vehicle} reload={reload} /></TabsContent>
          <TabsContent value="condition"><ConditionTab vehicle={vehicle} reload={reload} /></TabsContent>
          <TabsContent value="description"><DescriptionTab vehicle={vehicle} reload={reload} /></TabsContent>
          <TabsContent value="publish"><PublishTab vehicle={vehicle} reload={reload} /></TabsContent>
          {isAdmin && <TabsContent value="market"><MargeTab vehicle={vehicle} reload={reload} /></TabsContent>}
          <TabsContent value="history"><HistoryTab vehicle={vehicle} /></TabsContent>
        </div>
      </Tabs>

      <AIAssistantPanel open={assistantOpen} onOpenChange={setAssistantOpen} vehicle={vehicle} />

      {/* Lösch-Bestätigung */}
      <AlertDialog open={deleteOpen} onOpenChange={(open) => { if (!deleteBusy) setDeleteOpen(open); }}>
        <AlertDialogContent data-testid="vehicle-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fahrzeug endgültig löschen?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-foreground">{s.marke} {s.modell}</span>
              {" "}(<span className="font-mono">{vehicle.bestandsnummer}</span>) wird mit allen Fotos, Aufgaben,
              Notizen und der Preishistorie unwiderruflich entfernt. Diese Aktion kann nicht rückgängig gemacht werden.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteBusy} data-testid="vehicle-delete-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDelete(); }}
              disabled={deleteBusy}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid="vehicle-delete-confirm"
            >
              {deleteBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1.5" />}
              Endgültig löschen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verkaufspreis bearbeiten (mit KI-Vorschlag) */}
      <Dialog open={priceOpen} onOpenChange={setPriceOpen}>
        <DialogContent data-testid="price-edit-dialog">
          <DialogHeader><DialogTitle>Verkaufspreis festlegen</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="price-edit-input">Verkaufspreis (EUR)</Label>
              <Input id="price-edit-input" type="number" min="0" value={priceValue} onChange={(e) => setPriceValue(e.target.value)}
                placeholder="z.B. 28900" className="h-11" data-testid="price-edit-input" />
            </div>
            <div className="rounded-lg border border-primary/25 bg-primary/5 p-3 space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-medium flex items-center gap-1.5">
                  <Sparkles className="h-3.5 w-3.5 text-primary" /> KI-Preisvorschlag
                  <span className="rounded-full border border-primary/30 bg-primary/10 text-primary text-[10px] px-1.5 py-0.5">Schätzung</span>
                </span>
                <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-primary hover:bg-primary/10"
                  disabled={suggestBusy} onClick={generateSuggestion} data-testid="price-dialog-suggest-button">
                  {suggestBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                  {suggestBusy ? "KI berechnet..." : vehicle.price_suggestion ? "Neu berechnen" : "KI fragen"}
                </Button>
              </div>
              {vehicle.price_suggestion ? (
                <div className="space-y-1.5" data-testid="price-dialog-suggestion">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-sm tabular-nums text-muted-foreground">{fmtEUR(vehicle.price_suggestion.preis_von)} – {fmtEUR(vehicle.price_suggestion.preis_bis)}</span>
                    <span className="font-display font-bold text-primary tabular-nums">{fmtEUR(vehicle.price_suggestion.empfohlener_preis)}</span>
                    <Button type="button" size="sm" variant="outline" className="h-7 text-xs gap-1 border-primary/40 text-primary hover:bg-primary/10"
                      onClick={() => setPriceValue(String(vehicle.price_suggestion.empfohlener_preis))} data-testid="price-dialog-suggestion-apply">
                      <CheckCircle2 className="h-3 w-3" /> Übernehmen
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">{vehicle.price_suggestion.disclaimer}</p>
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">Unsicher beim Preis? Die KI gibt eine Orientierung auf Basis der Fahrzeugdaten – ohne Live-Marktdaten.</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPriceOpen(false)} data-testid="price-edit-cancel">{t("common.cancel")}</Button>
            <Button onClick={savePrice} disabled={priceBusy} data-testid="price-edit-save">
              {priceBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Preis speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={soldOpen} onOpenChange={(open) => { setSoldOpen(open); if (!open) setSoldSummary(null); }}>
        <DialogContent data-testid="sold-dialog">
          {!soldSummary ? (
            <>
              <DialogHeader><DialogTitle>Fahrzeug als verkauft markieren</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">
                Alle aktiven Kanäle werden deaktiviert. Die Fahrzeughistorie bleibt vollständig erhalten.
              </p>
              <div className="space-y-3">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Tatsächlicher Verkaufspreis (EUR)</Label>
                    <Input type="number" min="0" value={soldPrice} onChange={(e) => setSoldPrice(e.target.value)} className="h-11" data-testid="sold-price-input" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Verkaufsdatum</Label>
                    <Input type="date" value={soldDate} onChange={(e) => setSoldDate(e.target.value)} className="h-11" data-testid="sold-date-input" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Verkaufskanal</Label>
                    <Select value={soldChannel} onValueChange={setSoldChannel}>
                      <SelectTrigger className="h-11" data-testid="sold-channel-select"><SelectValue placeholder="Kanal wählen..." /></SelectTrigger>
                      <SelectContent>
                        {SALES_CHANNELS.map((c) => (
                          <SelectItem key={c.key} value={c.key} data-testid={`sold-channel-${c.key}`}>{c.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Verkaufsland / Zielmarkt <span className="text-muted-foreground text-xs">(optional)</span></Label>
                    <Input value={soldCountry} onChange={(e) => setSoldCountry(e.target.value)} placeholder="z.B. DE, AT, Export" className="h-11" data-testid="sold-country-input" />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setSoldOpen(false)} data-testid="sold-cancel-button">{t("common.cancel")}</Button>
                <Button onClick={markSold} disabled={busy} data-testid="sold-confirm-button">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verkauf bestätigen"}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <BadgeCheck className="h-5 w-5 text-emerald-400" /> Verkauf erfasst
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-1 text-sm" data-testid="sold-summary">
                {[
                  isAdmin ? ["Einkaufspreis (intern)", fmtEUR(soldSummary.purchase_price), "sold-summary-ek"] : null,
                  ["Erster Inseratspreis", fmtEUR(soldSummary.first_listing_price), "sold-summary-first-price"],
                  ["Letzter Inseratspreis", fmtEUR(soldSummary.last_listing_price), "sold-summary-last-price"],
                  ["Tatsächlicher Verkaufspreis", fmtEUR(soldSummary.sale_price), "sold-summary-sale-price"],
                  isAdmin ? ["Eingetragene Kosten", fmtEUR(soldSummary.costs_total), "sold-summary-costs"] : null,
                  ["Preisnachlass", soldSummary.preisnachlass != null ? `${fmtEUR(soldSummary.preisnachlass)}${soldSummary.preisnachlass_pct != null ? ` (${soldSummary.preisnachlass_pct}%)` : ""}` : "–", "sold-summary-discount"],
                  ["Standtage", soldSummary.days_in_stock != null ? `${soldSummary.days_in_stock} Tage` : "–", "sold-summary-days"],
                  soldSummary.sale_channel ? ["Verkaufskanal", SALES_CHANNEL_LABELS[soldSummary.sale_channel] || soldSummary.sale_channel, "sold-summary-channel"] : null,
                  soldSummary.sale_country ? ["Verkaufsland / Zielmarkt", soldSummary.sale_country, "sold-summary-country"] : null,
                ].filter(Boolean).map(([label, val, tid]) => (
                  <div key={label} className="flex justify-between gap-3 py-1.5 border-b border-border/50" data-testid={tid}>
                    <span className="text-muted-foreground">{label}</span>
                    <span className="tabular-nums text-right">{val}</span>
                  </div>
                ))}
                {isAdmin && (
                  <div className="flex justify-between gap-3 pt-2.5" data-testid="sold-summary-margin">
                    <span className="font-medium">Realisierte Marge nach eingetragenen Kosten</span>
                    {soldSummary.realisierte_marge_nach_eingetragenen_kosten == null ? (
                      <span className="text-muted-foreground">– (Einkaufspreis fehlt)</span>
                    ) : (
                      <span className={`font-display font-bold tabular-nums ${soldSummary.realisierte_marge_nach_eingetragenen_kosten < 0 ? "text-red-300" : "text-emerald-300"}`}>
                        {fmtEUR(soldSummary.realisierte_marge_nach_eingetragenen_kosten)}
                      </span>
                    )}
                  </div>
                )}
                {isAdmin && (
                  <p className="text-[10px] text-muted-foreground pt-1.5">
                    Details jederzeit im Tab „Markt &amp; Marge“. Interne Werte sind nur für Inhaber &amp; Manager sichtbar.
                  </p>
                )}
              </div>
              <DialogFooter>
                {isAdmin && (
                  <Button variant="outline" onClick={() => { setSoldOpen(false); setSoldSummary(null); setSearchParams({ tab: "market" }); }} data-testid="sold-summary-goto-marge">
                    Zu Markt &amp; Marge
                  </Button>
                )}
                <Button onClick={() => { setSoldOpen(false); setSoldSummary(null); }} data-testid="sold-summary-close">Fertig</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
